// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    TableBody,
    TextField,
    Button,
    Select,
    TablePagination,
    MenuItem,
    InputLabel,
    FormControl,
    Chip,
    InputAdornment,
    Checkbox,
    FormControlLabel,
} from '@mui/material';

import { capitalizeFirstLetter, renderKr } from 'utils/formatter';
import { teal } from 'api';
import { getAccessOrders } from 'store/slices/orders';
import paths from 'paths';

import { selectAccessOrders } from 'store';
import { getStatusName } from 'utils/constants';
import CreateOrderButton from './CreateOrderButton';
import { useOrdersQueryState } from './hooks/useOrdersQueryState';
import { useAccessProductOptions } from './hooks/useAccessProductOptions';
import ProductOptionsSelect from './ProductOptionsSelect';
import {
    calculateOrderTotal,
    getAdvisorDisplayName,
    getAttributionLabel,
    getCustomerDisplayName,
    getProductsLabel,
    isSubscriptionOrder,
} from './services/ordersTable';
import { collectAllOrdersForExport, createOrdersCsv } from './services/ordersCsv';
import { filterVisibleOrders, getVisibleStatusOptions, sanitizeStatusFilters } from './services/ordersVisibility';

const debounceSearch = (func, timeout = 300) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
};

const renderDateTimeLongWithYear = (date) => new Date(date).toLocaleString('da-DK', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

const dateColumnSx = {
    minWidth: 240,
    whiteSpace: 'nowrap',
};

const Orders = () => {
    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const user = useSelector((state) => state.auth.user);
    const organisation = useSelector((state) => state.auth.user.organisations.find((org) => org.organisationId === organisationId));
    const loadedOrders = useSelector((state) => state.accessOrders.loaded);
    const loading = useSelector((state) => state.accessOrders.loading);
    const count = useSelector((state) => state.accessOrders.count);
    const orders = useSelector(selectAccessOrders);
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [showSpamOrders, setShowSpamOrders] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const {
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        showCancelled,
        setShowCancelled,
        filters,
        searchRef,
        buildParams,
        syncUrl,
        updateFilter,
        clearFilters,
    } = useOrdersQueryState();

    const navigate = useNavigate();

    const showLegal = organisation.organisationId === 'jura-nord-advokater-aps' || organisation.organisationId === 'alliance-advokater';
    const { lawProductOptions, nonLawProductOptions } = useAccessProductOptions(organisationId, showLegal);
    const selectedAdvisorId = filters.find((x) => x.property === 'assignedTo.userId')?.value ?? '';
    const selectedStatus = filters.find((x) => x.property === 'status')?.value ?? '';
    const visibleStatusOptions = useMemo(() => getVisibleStatusOptions(showCancelled, organisation), [showCancelled, organisation]);
    const requestFilters = useMemo(() => sanitizeStatusFilters(filters, showCancelled), [filters, showCancelled]);
    const visibleOrders = useMemo(
        () => filterVisibleOrders(orders, requestFilters, showCancelled, showSpamOrders),
        [orders, requestFilters, showCancelled, showSpamOrders],
    );
    const advisorOptions = organisationUsers
        .map((advisor) => ({ ...advisor, resolvedUserId: advisor.userId || advisor.id }))
        .filter((advisor) => advisor?.resolvedUserId && advisor.resolvedUserId !== user.userId)
        .map((advisor) => ({
            userId: advisor.resolvedUserId,
            name: advisor.name || `${advisor.firstName || ''} ${advisor.lastName || ''}`.trim() || advisor.email || advisor.resolvedUserId,
        }));

    const fetchOrders = useCallback(() => {
        const urlParams = buildParams();
        const requestParams = new URLSearchParams(urlParams);

        if (requestFilters.length > 0) {
            requestParams.set('filter', JSON.stringify(requestFilters));
        } else {
            requestParams.delete('filter');
        }

        dispatch(getAccessOrders(organisationId, requestParams));
        syncUrl(urlParams);
    }, [buildParams, dispatch, organisationId, requestFilters, syncUrl]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        let active = true;

        const fetchOrganisationUsers = async () => {
            const usersInOrganisation = await teal.getUsersInOrganisation(organisationId);
            if (active) {
                setOrganisationUsers(usersInOrganisation || []);
            }
        };

        fetchOrganisationUsers();
        return () => {
            active = false;
        };
    }, [organisationId]);

    const handleShowCancelledChange = (_, checked) => {
        setShowCancelled(checked);
        setPage(0);

        if (!checked && selectedStatus === 'cancelled') {
            updateFilter('status', 'equals', '');
        }
    };

    const handleChangeSorting = (newSortBy) => {
        setSortBy(newSortBy);
        const flippedSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        let newSortOrder = 'asc';
        if (sortBy === newSortBy) {
            newSortOrder = flippedSortOrder;
        } else if (newSortBy === 'orderNumber') {
            newSortOrder = 'desc';
        }
        setSortOrder(newSortOrder);
    };

    const handleSearchChange = useMemo(
        () => debounceSearch((e) => setSearch(e.target.value)),
        [setSearch],
    );

    const getOrderPath = (orderId) => paths.penlyAccessOrder.getPath(organisationId, orderId);

    const handleExportCsv = useCallback(async () => {
        setIsExporting(true);

        try {
            const allOrders = await collectAllOrdersForExport({
                organisationId,
                baseParams: buildParams(),
                requestFilters,
                getAllOrders: teal.getAllOrders,
            });

            const exportableOrders = filterVisibleOrders(allOrders, requestFilters, showCancelled, showSpamOrders);
            const rows = exportableOrders.map((order) => {
                const totalPrice = calculateOrderTotal(order.items);
                const hasSubscription = isSubscriptionOrder(order.items);
                const createdAt = order.createdAt ? capitalizeFirstLetter(renderDateTimeLongWithYear(order.createdAt)) : '';
                const meetingStartTime = order.bookings?.[0]?.startTime;
                const meetingAt = meetingStartTime ? capitalizeFirstLetter(renderDateTimeLongWithYear(meetingStartTime)) : '';
                const isMeetingReady = order.dataCollectionProgress?.currentStep
                    ? (order.dataCollectionProgress.completed ? 'Ja' : 'Nej')
                    : '';

                return {
                    orderNumber: order.orderNumber,
                    customerName: getCustomerDisplayName(order),
                    customerEmail: order.customer?.email ?? '',
                    createdAt,
                    meetingAt,
                    advisorName: getAdvisorDisplayName(order),
                    hasSharing: order.sharingIds?.[0] ? 'Ja' : 'Nej',
                    isMeetingReady,
                    products: getProductsLabel(order.items),
                    price: hasSubscription ? `${renderKr(totalPrice)} / år` : renderKr(totalPrice),
                    status: order.status ? getStatusName(order.status, organisation) : '',
                    attribution: getAttributionLabel(order),
                };
            });

            const csvContent = createOrdersCsv(rows);
            const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const dateString = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.download = `orders-${organisationId}-${dateString}.csv`;
            link.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            alert('Kunne ikke eksportere CSV'); // eslint-disable-line no-alert
        } finally {
            setIsExporting(false);
        }
    }, [buildParams, organisation, organisationId, requestFilters, showCancelled, showSpamOrders]);

    return (
        <>
            { !loadedOrders && (
                <div className="App-section-loading">
                    <CircularProgress color="primary" size={ 40 } />
                </div>
            ) }
            { loadedOrders && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 16, minWidth: 1300 }}>
                        <TextField
                            size="small"
                            placeholder="Søg ordrer..."
                            onChange={ handleSearchChange }
                            inputRef={ searchRef }
                            autoComplete="off"
                            sx={{
                                width: 360,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                paddingLeft: 1,
                                paddingRight: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <CreateOrderButton disabled={ loading } isLaw={ showLegal } customer={ undefined } />
                            <Button disabled={ loading } endIcon={ <RefreshIcon /> } onClick={ fetchOrders }>
                                { loading ? 'Opdaterer' : 'Opdatér' }
                            </Button>
                            <Button disabled={ loading || isExporting } onClick={ handleExportCsv }>
                                { isExporting ? 'Eksporterer...' : 'Eksportér' }
                            </Button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <ProductOptionsSelect
                            labelId="product-label"
                            label="Produkt"
                            value={ filters.find((x) => x.property === 'items.product.id')?.value ?? '' }
                            onChange={ (e) => updateFilter('items.product.id', 'equals', e.target.value) }
                            lawProductOptions={ lawProductOptions }
                            nonLawProductOptions={ nonLawProductOptions }
                            variant="standard"
                            sx={{ m: 1, minWidth: 120 }}
                            allOptionLabel="Alle"
                        />
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                value={ selectedStatus === 'cancelled' && !showCancelled ? '' : selectedStatus }
                                onChange={ (e) => updateFilter('status', 'equals', e.target.value) }
                                label="Status"
                            >
                                <MenuItem value="">Alle</MenuItem>
                                { visibleStatusOptions.map((status) => (
                                    <MenuItem key={ status.value } value={ status.value }>
                                        { status.name }
                                    </MenuItem>
                                )) }
                            </Select>
                        </FormControl>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 140 }}>
                            <InputLabel id="orders-scope-label">Rådgiver</InputLabel>
                            <Select
                                labelId="orders-scope-label"
                                value={ selectedAdvisorId === user.userId ? 'mine' : selectedAdvisorId }
                                onChange={ (e) => updateFilter('assignedTo.userId', 'equals', e.target.value === 'mine' ? user.userId : e.target.value) }
                                label="Ordrer"
                            >
                                <MenuItem value="">Alle</MenuItem>
                                <MenuItem value="mine">Mig</MenuItem>
                                { advisorOptions.map((advisor) => (
                                    <MenuItem key={ advisor.userId } value={ advisor.userId }>{ advisor.name }</MenuItem>
                                )) }
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            sx={{ mt: 2, ml: 1 }}
                            control={ <Checkbox checked={ showCancelled } onChange={ handleShowCancelledChange } /> }
                            label="Vis annullerede ordrer"
                        />
                        <FormControlLabel
                            sx={{ mt: 2, ml: 1 }}
                            control={ <Checkbox checked={ showSpamOrders } onChange={ (_, checked) => setShowSpamOrders(checked) } /> }
                            label="Vis spam ordrer"
                        />
                        <Button style={{ marginTop: 20 }} onClick={ clearFilters }>Ryd filtre</Button>
                    </div>
                    <TableContainer sx={{ mt: 0 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'orderNumber' } direction={ sortBy === 'orderNumber' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('orderNumber') }>
                                            Ordre #
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'customer.email' } direction={ sortBy === 'customer.email' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('customer.email') }>
                                            Kunde
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left" sx={ dateColumnSx }>
                                        <TableSortLabel active={ sortBy === 'createdAt' } direction={ sortBy === 'createdAt' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('createdAt') }>
                                            Bestillingsdato
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left" sx={ dateColumnSx }>
                                        <TableSortLabel active={ sortBy === 'bookings.startTime' } direction={ sortBy === 'bookings.startTime' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('bookings.startTime') }>
                                            Mødedato
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'assignedAdvisor.name' } direction={ sortBy === 'assignedAdvisor.name' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('assignedAdvisor.name') }>
                                            Rådgiver
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'sharingIds' } direction={ sortBy === 'sharingIds' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('sharingIds') }>
                                            Deling
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">Mødeklar</TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'items.product.name' } direction={ sortBy === 'items.product.name' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('items.product.name') }>
                                            Produkter
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="left">Pris</TableCell>
                                    <TableCell align="left">
                                        <TableSortLabel active={ sortBy === 'status' } direction={ sortBy === 'status' ? sortOrder : 'asc' } onClick={ () => handleChangeSorting('status') }>
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { visibleOrders.map((order) => {
                                    const totalPrice = calculateOrderTotal(order.items);
                                    const hasSubscription = isSubscriptionOrder(order.items);
                                    const customerDisplayName = getCustomerDisplayName(order);
                                    const showCustomerEmail = Boolean(order.customer?.email && order.customer?.userId);

                                    return (
                                        <TableRow key={ order.orderId } onClick={ () => navigate(getOrderPath(order.orderId)) } style={{ cursor: 'pointer' }}>
                                            <TableCell align="left" sx={{ fontWeight: 700 }}>{ order.orderNumber }</TableCell>
                                            <TableCell align="left">
                                                <div>{ customerDisplayName }</div>
                                                { showCustomerEmail && <div style={{ fontSize: '0.8em', color: '#666' }}>{ order.customer.email }</div> }
                                            </TableCell>
                                            <TableCell align="left" sx={ dateColumnSx }>{ capitalizeFirstLetter(renderDateTimeLongWithYear(order.createdAt)) }</TableCell>
                                            <TableCell align="left" sx={ dateColumnSx }>
                                                { order.bookings && order.bookings[0] ? capitalizeFirstLetter(renderDateTimeLongWithYear(order.bookings[0].startTime)) : '' }
                                            </TableCell>
                                            <TableCell align="left">{ getAdvisorDisplayName(order) }</TableCell>
                                            <TableCell align="left">
                                                <Chip label={ order.sharingIds && order.sharingIds[0] ? 'Ja' : 'Nej' } />
                                            </TableCell>
                                            <TableCell align="left">
                                                { order.dataCollectionProgress?.currentStep && (
                                                    order.dataCollectionProgress.completed
                                                        ? <Chip color="primary" label="Ja" />
                                                        : <Chip label="Nej" />
                                                ) }
                                            </TableCell>
                                            <TableCell align="left">{ getProductsLabel(order.items) }</TableCell>
                                            <TableCell align="left">
                                                { hasSubscription ? `${renderKr(totalPrice)} / år` : renderKr(totalPrice) }
                                            </TableCell>
                                            <TableCell align="left">
                                                <Chip label={ getStatusName(order.status, organisation) } />
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={ count }
                        page={ page || 0 }
                        onPageChange={ (_, newPage) => setPage(newPage) }
                        rowsPerPage={ pageSize || 50 }
                        onRowsPerPageChange={ (event) => {
                            setPageSize(parseInt(event.target.value, 10));
                            setPage(0);
                        } }
                        labelRowsPerPage="Rækker pr. side"
                        labelDisplayedRows={ ({ from, to, count: totalCount }) => `${from}-${to} af ${totalCount}` }
                    />
                </>
            ) }
        </>
    );
};

export default Orders;
